import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Twitter, Linkedin, Send, Mail, Copy, Loader2, ShieldAlert, AlertOctagon, Lock, Search, FileSearch, Bell, Sparkles, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChainMarquee from "../components/ChainMarquee";
import ConsultCtaBand from "../components/ConsultCtaBand";
import { QelliPublicCTA } from "../components/qelli/QelliPublicCTA";
import Typewriter from "../components/Typewriter";
import { useUserAuth } from "../lib/userAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import useQelliWarmPulse from "../hooks/useQelliWarmPulse";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANNOUNCE_TWEET =
  "I just ran a free post-quantum exposure scan with @qortrace — they cover 18 chains incl. Cosmos & Move-VM. " +
  "Run yours: ";

export default function FreeScan() {
  useDocumentTitle("Free PQC Wallet Scan — QorTrace");
  usePageMeta({
    title: "Free PQC Wallet Scan — QorTrace",
    description: "Get your wallet's post-quantum readiness score in seconds. Free, no card required, 18 chains supported including Bitcoin, Ethereum, Solana, Cosmos.",
    canonicalPath: "/free-scan",
  });
  useEffect(() => {
    trackEvent("freescan_started", { surface: "free-scan" });
  }, []);

  // Iter198 — Qelli warm-pulse on /free-scan for shared landings.
  useQelliWarmPulse({
    enabled:    true,
    source:     "freescan-landing",
    intentText: "Want me to walk you through your scan result?",
    prompt:
      "I'm on the free PQC wallet scan page. Walk me through what " +
      "the scanner checks, what the tier classifications mean, and " +
      "what the right next step is once I see my result.",
  });
  const { account, loading, signup, login, client, setMarketing } = useUserAuth();

  const shareUrl = (typeof window !== "undefined")
    ? `${window.location.origin}/free-scan?utm_source=share&utm_medium=social&utm_campaign=qortrace`
    : "https://qortrace.com/free-scan";

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="free-scan-page">
      <Header />

      <section className="relative qt-grain">
        <div className="absolute inset-0 qt-hero-grid pointer-events-none" />
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-10 relative">
          <Link to="/free-scan" data-testid="freescan-launch-pill"
                className="inline-flex items-center gap-2 mb-5 qt-mono text-xs px-3 py-1.5 rounded-full"
                style={{ background: "rgba(0,229,255,0.06)", color: "var(--qt-cyan)", border: "1px solid var(--qt-cyan)", letterSpacing: "0.05em" }}>
            <span className="qt-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: "var(--qt-cyan)" }} />
            NOW LIVE · 18 CHAINS · REAL-TIME DATA & ANALYSIS
          </Link>
          <h1 className="qt-display text-[36px] sm:text-[52px] xl:text-[64px]" style={{ lineHeight: 1.04, letterSpacing: "-0.015em" }}>
            The First PQC Scanner &amp; Auditor<br />
            to <span style={{ color: "#00E5FF" }}>Cover 18 Chains, including</span><br />
            <span style={{ color: "#00E5FF" }}>Institutional Vulnerabilities.</span>
          </h1>
          <p className="mt-6 text-lg max-w-3xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            18 chains. 0 fluff. Run a deterministic post-quantum exposure scan on any wallet
            in 30 seconds. Your free preview is emailed after you share the launch.
          </p>

          <div className="mt-8"><ChainMarquee size={36} gap={28} /></div>
        </div>
      </section>

      {/* HOW IT WORKS — 3-step strip with chevron connectors. Brings
          FreeScan in line with the structured product pages by visually
          setting expectations BEFORE the user hits the auth gate. */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 mt-2"
               data-testid="freescan-how-it-works">
        <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
          HOW THE FREE SCAN WORKS · 3 STEPS · 30 SECONDS
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
          {[
            { Icon: Lock,       step: "01", t: "Create a free account",
              d: "Email + password — no credit card. We email your watermarked preview." },
            { Icon: Search,     step: "02", t: "Pick a chain & paste a wallet",
              d: "18 chains supported. Real-time balance, pubkey-exposure check, deterministic score." },
            { Icon: FileSearch, step: "03", t: "Share to unlock the report",
              d: "One click on X / LinkedIn / Telegram and your preview lands in your inbox." },
          ].flatMap((x, i, arr) => {
            const card = (
              <div key={`card-${i}`} className="qt-card relative"
                   data-testid={`freescan-step-${i+1}`}
                   style={{
                     background: "linear-gradient(135deg, rgba(0,229,255,0.04), rgba(155,124,255,0.02))",
                     borderColor: "rgba(0,229,255,0.18)",
                   }}>
                <div className="flex items-center gap-3">
                  <div className="qt-mono text-xs px-2 py-0.5 rounded"
                       style={{ color: "var(--qt-cyan)",
                                border: "1px solid var(--qt-cyan)",
                                fontWeight: 700, letterSpacing: "0.10em",
                                background: "rgba(0,229,255,0.06)" }}>
                    {x.step}
                  </div>
                  <x.Icon size={16} style={{ color: "var(--qt-cyan)" }} />
                </div>
                <div className="qt-h2 text-lg mt-3">{x.t}</div>
                <p className="mt-1.5 text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                  {x.d}
                </p>
              </div>
            );
            // Insert a chevron connector between steps (md+ only — collapses
            // gracefully on mobile to plain stacked cards with no arrow).
            if (i < arr.length - 1) {
              return [card, (
                <div key={`arrow-${i}`}
                     className="hidden md:flex items-center justify-center"
                     aria-hidden
                     data-testid={`freescan-step-connector-${i}`}
                     style={{ color: "var(--qt-cyan)", opacity: 0.7 }}>
                  <ChevronRight size={28} strokeWidth={2.5} />
                </div>
              )];
            }
            return [card];
          })}
        </div>
        {/* Vertical buffer — separates the 3-step strip from the Step 1
            account-creation card so they don't visually run into each other. */}
        <div className="mt-10 mb-4 flex items-center justify-center"
             aria-hidden
             data-testid="freescan-howto-divider">
          <div className="h-px flex-1"
               style={{ background: "linear-gradient(to right, transparent, var(--qt-border), transparent)" }} />
          <div className="qt-mono text-[10px] px-3"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
            START BELOW
          </div>
          <div className="h-px flex-1"
               style={{ background: "linear-gradient(to left, transparent, var(--qt-border), transparent)" }} />
        </div>
      </section>

      {/* AUTH GATE → FORM → SHARE GATE */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 -mt-2 pb-10">
        {loading ? (
          <div className="qt-card text-sm" style={{ color: "var(--qt-text-2)" }}>Loading…</div>
        ) : !account ? (
          <AuthGate signup={signup} login={login} />
        ) : (
          <ScanFlow account={account} client={client} setMarketing={setMarketing} shareUrl={shareUrl} />
        )}
      </section>

      {/* FREE vs PRO — slim upgrade teaser. Mirrors the upgrade rail other
          product pages have. Always visible (not gated behind a result). */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 mt-2 pb-12"
               data-testid="freescan-tier-rail">
        <div className="qt-card grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
             style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.04), rgba(155,124,255,0.04))",
                      border: "1px solid var(--qt-border)" }}>
          <div className="md:col-span-7">
            <div className="qt-label flex items-center gap-2"
                 style={{ color: "var(--qt-cyan)" }}>
              <Sparkles size={11} /> WHAT'S IN THE FULL DEEP-DIVE
            </div>
            <h3 className="qt-h2 text-2xl sm:text-3xl mt-2">
              Free preview vs <span style={{ color: "var(--qt-cyan)" }}>QorTrace Pro</span>
            </h3>
            <ul className="mt-4 space-y-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
              <TierRow free={false}  pro={true}  text="Unlimited deterministic scans across all 18 chains" />
              <TierRow free={false}  pro={true}  text="Watermark-free PDF + dynamic SVG/PNG certificate" />
              <TierRow free={false}  pro={true}  text="Continuous monitoring — alert when pubkey exposure changes" />
              <TierRow free={true}   pro={true}  text="Real-time chain data + deterministic exposure score" />
              <TierRow free={true}   pro={true}  text="Methodology grounding (qortrace-method-v0.1)" />
            </ul>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link to="/pricing" data-testid="freescan-upgrade-cta"
                  className="qt-btn qt-btn-primary justify-center"
                  style={{ fontSize: 14 }}>
              See Pro pricing <ArrowRight size={14} />
            </Link>
            <Link to="/atlas" data-testid="freescan-atlas-link"
                  className="qt-btn qt-btn-ghost justify-center"
                  style={{ fontSize: 13, border: "1px solid var(--qt-border)" }}>
              <Bell size={13} /> Add continuous monitoring (Atlas)
            </Link>
            <p className="qt-mono text-[10px] mt-1"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
              ANNUAL BILLING SAVES 17% · CANCEL ANYTIME
            </p>
          </div>
        </div>
      </section>

      {/* SHARE BAND — public, no auth needed */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 pb-20">
        <div data-testid="share-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>SHARE THE LAUNCH</div>
              <div className="qt-h2 text-2xl sm:text-3xl mt-1" style={{ color: "#fff" }}>
                Run a free post-quantum scan in 30 seconds.
              </div>
              <p className="mt-2 text-base" style={{ color: "var(--qt-text-2)" }}>
                Help Builders on your chain hear about QorTrace — share the card, your network gets safer this way.
              </p>
            </div>
            <a href="/og-coverage-launch.png" download data-testid="free-download-og" className="qt-btn qt-btn-secondary">
              Download PNG
            </a>
          </div>

          {/* Branded share buttons */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="public-share-row">
            <ShareLink
              testid="public-share-twitter"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(ANNOUNCE_TWEET)}&url=${encodeURIComponent(shareUrl)}&hashtags=qortrace,postquantum`}
              bg="#000000" border="#00E5FF" fg="#FFFFFF" label="X / Twitter" Icon={Twitter} />
            <ShareLink
              testid="public-share-linkedin"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              bg="#0A66C2" border="#0A66C2" fg="#FFFFFF" label="LinkedIn" Icon={Linkedin} />
            <ShareLink
              testid="public-share-telegram"
              href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(ANNOUNCE_TWEET)}`}
              bg="#229ED9" border="#229ED9" fg="#FFFFFF" label="Telegram" Icon={Send} />
            <ShareLink
              testid="public-share-email"
              href={`mailto:?subject=${encodeURIComponent("QorTrace post-quantum scanner — 18 chains, free preview")}&body=${encodeURIComponent(ANNOUNCE_TWEET + shareUrl)}`}
              bg="#1F2937" border="#9CAEC9" fg="#FFFFFF" label="Email" Icon={Mail} />
            <PublicCopyButton text={ANNOUNCE_TWEET + shareUrl} />
          </div>

          <div className="mt-5 qt-card" style={{ padding: 0, overflow: "hidden", border: "1px solid var(--qt-border)" }}>
            <img src="/og-coverage-launch.png?v=26" alt="QorTrace 18-chain coverage" data-testid="og-card-preview"
                 style={{ width: "100%", display: "block" }} />
          </div>
          <Typewriter
            text="Drop into Twitter/X, LinkedIn, Telegram, or your protocol's monthly update."
            speed={28}
            delay={250}
            testid="under-card-typewriter"
            className="mt-4 qt-display text-lg sm:text-xl"
            style={{ color: "var(--qt-cyan)", letterSpacing: "0.02em", fontWeight: 700 }}
          />
        </div>
      </section>

      <ConsultCtaBand source="freescan-bottom-cta" variant="card" />
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 pb-12">
        <QelliPublicCTA variant="inline" />
      </div>
      <Footer />
    </div>
  );
}

/* --------------------------- AUTH GATE -------------------------------- */
function AuthGate({ signup, login }) {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ email: "", password: "", name: "", marketing_opt_in: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "signup") {
        await signup(form);
      } else {
        await login({ email: form.email, password: form.password });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div className="qt-card qt-grain" data-testid="free-scan-auth-gate">
      <div className="flex items-center gap-3">
        <Lock size={16} style={{ color: "var(--qt-cyan)" }} />
        <div className="qt-label">{mode === "signup" ? "STEP 1 · CREATE FREE ACCOUNT" : "STEP 1 · SIGN IN"}</div>
      </div>
      <h2 className="qt-h2 text-2xl sm:text-3xl mt-2">
        {mode === "signup" ? "Free QorTrace account — 30 seconds." : "Welcome back."}
      </h2>
      <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
        Free accounts get one free preview scan per address. Upgrade for unlimited deep-dive analysis,
        continuous monitoring, and watermark-free reports.
      </p>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="auth-form">
        {mode === "signup" && (
          <div className="md:col-span-2">
            <div className="qt-label mb-2" id="auth-name-label">YOUR NAME (optional)</div>
            <input className="qt-input" value={form.name} data-testid="auth-name"
                   aria-labelledby="auth-name-label"
                   onChange={(e) => set("name", e.target.value)} />
          </div>
        )}
        <div>
          <div className="qt-label mb-2" id="auth-email-label">EMAIL</div>
          <input className="qt-input" required value={form.email}
                 data-testid="auth-email" placeholder="you@protocol.xyz"
                 aria-labelledby="auth-email-label"
                 type="email" autoComplete="email"
                 onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <div className="qt-label mb-2" id="auth-password-label">PASSWORD</div>
          <input type="password" className="qt-input" required value={form.password}
                 data-testid="auth-password"
                 placeholder={mode === "signup" ? "9+ chars · upper/lower/number/special" : "Your password"}
                 aria-labelledby="auth-password-label"
                 autoComplete={mode === "signup" ? "new-password" : "current-password"}
                 onChange={(e) => set("password", e.target.value)} />
        </div>
        {mode === "signup" && (
          <div className="md:col-span-2 flex items-start gap-2 text-xs" style={{ color: "var(--qt-text-3)" }}>
            <input type="checkbox" id="opt-in" checked={form.marketing_opt_in}
                   data-testid="auth-optin"
                   onChange={(e) => set("marketing_opt_in", e.target.checked)} style={{ marginTop: 3 }} />
            <label htmlFor="opt-in">
              I want to receive my free preview report by email + occasional QorTrace updates.
              You can unsubscribe one-click from any email.
            </label>
          </div>
        )}
        {error && (
          <div className="md:col-span-2 px-3 py-2 rounded text-sm"
               data-testid="auth-error"
               style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
            <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
          </div>
        )}
        <div className="md:col-span-2 flex flex-wrap items-center gap-3 justify-between">
          <button type="button" onClick={() => setMode(mode === "signup" ? "login" : "signup")}
                  data-testid="auth-toggle"
                  className="qt-mono text-xs" style={{ color: "var(--qt-cyan)", letterSpacing: "0.05em" }}>
            {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
          <button type="submit" disabled={busy} data-testid="auth-submit" className="qt-btn qt-btn-primary">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Working…</> : <>{mode === "signup" ? "Create account" : "Sign in"} <ArrowRight size={14} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------- SCAN FLOW ------------------------------ */
function ScanFlow({ account, client, setMarketing, shareUrl }) {
  const [chains, setChains] = useState([]);
  const [chain, setChain] = useState(() => {
    if (typeof window === "undefined") return "osmosis";
    const sp = new URLSearchParams(window.location.search);
    return (sp.get("chain") || "osmosis").toLowerCase();
  });
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [emailDelivered, setEmailDelivered] = useState(false);
  const [pledging, setPledging] = useState(false);
  const [pledgeError, setPledgeError] = useState("");

  useEffect(() => {
    axios.get(`${API}/chains`).then(({ data }) => {
      const list = data.chains || [];
      setChains(list);
      // Validate the URL-supplied chain id against the catalog. If it doesn't
      // exist (typo, removed chain, etc.) fall back to the default so the
      // dropdown is never stuck on something the backend won't accept.
      if (list.length && !list.some((c) => c.id === chain)) {
        setChain("osmosis");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!chain || !address.trim()) { setError("Pick a chain and paste an address."); return; }
    setBusy(true);
    try {
      const referrer = (typeof document !== "undefined" && document.referrer) ? document.referrer : "";
      const sp = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
      const utmSource = sp.get("utm_source") || "free-scan";
      const { data } = await client.post(`/early-access`, {
        chain, address: address.trim(), referrer, source: utmSource.slice(0, 60),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const pledge = async (channel, openUrl) => {
    if (!result?.lead?.id) return;
    setPledging(true); setPledgeError("");
    try {
      const { data } = await client.post(`/early-access/${result.lead.id}/pledge-share`);
      setEmailDelivered(!!data.email_sent || !!data.already_sent);
    } catch (err) {
      setPledgeError(err?.response?.data?.detail || "Could not send the report email — please try another share button or check your account opt-in.");
    }
    finally { setPledging(false); }
    if (openUrl) window.open(openUrl, "_blank", "noopener,noreferrer");
  };

  if (!result) {
    return (
      <div className="qt-card qt-grain" data-testid="free-scan-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="qt-label">STEP 2 · RUN YOUR FREE SCAN</div>
            <h2 className="qt-h2 text-2xl sm:text-3xl mt-2">Check any wallet in 30 seconds.</h2>
          </div>
          <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
            Signed in as <span style={{ color: "#fff" }}>{account.email}</span>
            {!account.marketing_opt_in && (
              <button onClick={() => setMarketing(true)}
                      data-testid="reopt-in"
                      className="ml-2 qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                · Re-enable marketing
              </button>
            )}
          </div>
        </div>

        {!account.marketing_opt_in && (
          <div className="mt-4 px-3 py-2 rounded text-sm"
               data-testid="optin-warning"
               style={{ background: "rgba(255,176,0,0.08)", border: "1px solid rgba(255,176,0,0.4)", color: "#FFB000" }}>
            You're opted out of marketing — your preview report can't be emailed until you re-enable it.
          </div>
        )}

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="free-scan-form">
          <div>
            <div className="qt-label mb-2">CHAIN</div>
            <select className="qt-select w-full" value={chain} data-testid="free-chain"
                    onChange={(e) => setChain(e.target.value)}>
              {[...chains].sort((a, b) => a.label.localeCompare(b.label)).map((c) => <option key={c.id} value={c.id}>{`${c.label} (${c.symbol})`}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="qt-label mb-2">WALLET ADDRESS</div>
            <input className="qt-input" required value={address}
                   placeholder="Paste any address from any of the 18 supported chains"
                   data-testid="free-address"
                   onChange={(e) => setAddress(e.target.value)} />
          </div>
          {error && (
            <div className="md:col-span-3 px-3 py-2 rounded text-sm"
                 data-testid="free-error"
                 style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
              <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
            </div>
          )}
          <div className="md:col-span-3 flex flex-wrap items-center gap-3 justify-between">
            <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
              1 free preview / address. Upgrade for unlimited.
            </div>
            <button type="submit" disabled={busy} data-testid="free-submit" className="qt-btn qt-btn-primary">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Scanning…</> : <>Run free scan <ArrowRight size={14} /></>}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // RESULT + SHARE GATE
  const tier = result.scan?.scoring?.tier;
  const score = result.scan?.scoring?.score;
  const tierColor = tier === "Critical" ? "#FF6E6E" : tier === "High" ? "#FFB000"
                   : tier === "Moderate" ? "#FFD60A" : "#1FDD83";

  // Iter128 — Track C piece 2: shares point at the OG-tagged SSR wrapper
  // /api/public/scan/<id>/share, which renders the score's OG card for
  // LinkedIn/X/Slack/Bluesky scrapers and 302-redirects humans on to
  // /scan/result/<id>. Falls back to the generic /free-scan if the scan
  // record is somehow missing.
  const scanId = result.scan?.id;
  const scanShareUrl = scanId
    ? `${API}/api/public/scan/${scanId}/share`
    : shareUrl;
  const scanResultLanding = scanId
    ? `${typeof window !== "undefined" ? window.location.origin : "https://qortrace.com"}/scan/result/${scanId}`
    : shareUrl;
  const personalTweet = (typeof score === "number")
    ? `I just got a PQC exposure score of ${Math.round(score)}/100 (${(tier||'').toLowerCase()}) on QorTrace's free post-quantum scanner. Run yours:`
    : ANNOUNCE_TWEET;

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(personalTweet)}&url=${encodeURIComponent(scanShareUrl)}&hashtags=qortrace,postquantum`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(scanShareUrl)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(scanShareUrl)}&text=${encodeURIComponent(personalTweet)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent("QorTrace post-quantum scanner — 18 chains, free preview")}&body=${encodeURIComponent(personalTweet + " " + scanResultLanding)}`;

  return (
    <div data-testid="free-scan-result-wrap" className="qt-card qt-grain" style={{ position: "relative", overflow: "hidden" }}>
      {/* Watermark */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "repeating-linear-gradient(-30deg, rgba(255,176,0,0.08) 0 80px, rgba(255,176,0,0.0) 80px 240px)",
        zIndex: 0,
      }} />
      <div aria-hidden style={{
        position: "absolute", top: 18, right: 24, fontFamily: "monospace", letterSpacing: "0.18em",
        fontSize: 11, color: "rgba(255,176,0,0.85)", border: "1px solid rgba(255,176,0,0.5)",
        padding: "4px 10px", borderRadius: 4, background: "rgba(255,176,0,0.06)", zIndex: 1,
      }}>FREE PREVIEW</div>

      <div style={{ position: "relative", zIndex: 1 }}>
        <div className="flex items-center gap-2">
          <Check size={16} style={{ color: "#1FDD83" }} />
          <div className="qt-label" style={{ color: "#1FDD83" }}>SCAN COMPLETE</div>
        </div>
        <h2 className="qt-h2 text-2xl sm:text-3xl mt-2">
          {result.scan.chain_label} · {result.scan.address.slice(0, 10)}…{result.scan.address.slice(-6)}
        </h2>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="qt-card" style={{ background: "rgba(4,9,20,0.55)" }}>
            <div className="qt-label">EXPOSURE SCORE</div>
            <div className="qt-display text-6xl mt-2" style={{ color: tierColor }}>{score}</div>
            <div className="qt-mono text-xs mt-1" style={{ color: tierColor }}>{tier?.toUpperCase()} TIER</div>
          </div>
          <div className="qt-card md:col-span-2" style={{ background: "rgba(4,9,20,0.55)" }}>
            <div className="qt-label">PUBKEY EXPOSED</div>
            <div className="text-2xl mt-1" style={{ color: result.scan.data.pubkey_exposed ? "#FF6E6E" : "#1FDD83" }}>
              {result.scan.data.pubkey_exposed ? "YES — quantum-recoverable" : "NO — currently hash-protected"}
            </div>
            <div className="qt-label mt-4">BALANCE</div>
            <div className="qt-mono text-sm mt-1" style={{ color: "#fff" }}>
              {Number(result.scan.data.balance_native ?? 0).toFixed(6)} {result.scan.data.symbol}
              {" · "}${Number(result.scan.data.balance_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Iter52.10 — RESULT-AWARE UPGRADE CTA. Morphs based on the actual
            scan outcome so the upgrade pitch is tied to what the user just
            saw, not a static tier rail. The most-converting variant in our
            funnel: personalized, immediate, single-CTA. */}
        <div className="mt-6 qt-card" data-testid="result-aware-upgrade"
             style={{
               background: "linear-gradient(135deg, rgba(0,229,255,0.08), rgba(155,124,255,0.04))",
               borderColor: "rgba(0,229,255,0.4)",
             }}>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="flex-1">
              <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
                <Sparkles size={11} className="inline -mt-0.5 mr-1" /> WHAT PRO WOULD UNLOCK FOR THIS WALLET
              </div>
              <h3 className="qt-h2 text-xl sm:text-2xl mt-2" style={{ color: "#fff" }}>
                {resultAwareHeadline(result)}
              </h3>
              <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                {resultAwareBody(result)}
              </p>
            </div>
            <div className="flex flex-col gap-2 md:min-w-[200px] w-full md:w-auto">
              <Link to="/pricing"
                    data-testid="result-aware-upgrade-cta"
                    className="qt-btn qt-btn-primary justify-center"
                    style={{ fontSize: 14 }}>
                Unlock the full report <ArrowRight size={14} />
              </Link>
              <Link to="/atlas"
                    data-testid="result-aware-monitor-cta"
                    className="qt-btn qt-btn-ghost justify-center"
                    style={{ fontSize: 12, border: "1px solid var(--qt-border)" }}>
                <Bell size={12} /> Monitor continuously
              </Link>
            </div>
          </div>
        </div>

        {/* SHARE GATE */}
        <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--qt-border)" }}>
          {emailDelivered ? (
            <div data-testid="email-delivered" className="qt-card"
                 style={{ background: "rgba(0,210,106,0.08)", border: "1px solid rgba(0,210,106,0.5)" }}>
              <div className="flex items-center gap-2"><Check size={16} style={{ color: "#1FDD83" }} />
                <div className="qt-label" style={{ color: "#1FDD83" }}>REPORT EMAILED · CHECK YOUR INBOX</div>
              </div>
              <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>
                Thanks for sharing! Your watermarked preview report is on its way to {account.email}.
                Want the full deep-dive PDF? <Link to="/pricing" style={{ color: "var(--qt-cyan)" }}>Upgrade your plan</Link>.
              </p>
            </div>
          ) : (
            <div data-testid="share-gate">
              <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>STEP 3 · SHARE TO UNLOCK YOUR REPORT</div>
              <h3 className="qt-h2 text-xl mt-2">Tap any share button below — your preview email lands instantly.</h3>
              <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
                Sharing helps the ecosystem migrate before quantum-era threats become operational.
                Use #qortrace · we'll see it.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button onClick={() => pledge("twitter", tweetUrl)} disabled={pledging}
                        data-testid="share-twitter" className="qt-btn qt-btn-primary">
                  <Twitter size={14}/> Share on X
                </button>
                <button onClick={() => pledge("linkedin", linkedinUrl)} disabled={pledging}
                        data-testid="share-linkedin" className="qt-btn qt-btn-secondary">
                  <Linkedin size={14}/> LinkedIn
                </button>
                <button onClick={() => pledge("telegram", telegramUrl)} disabled={pledging}
                        data-testid="share-telegram" className="qt-btn qt-btn-secondary">
                  <Send size={14}/> Telegram
                </button>
                <button onClick={() => pledge("email", mailtoUrl)} disabled={pledging}
                        data-testid="share-email" className="qt-btn qt-btn-secondary">
                  <Mail size={14}/> Email a colleague
                </button>
                <button onClick={async () => {
                          try { await navigator.clipboard.writeText(ANNOUNCE_TWEET + shareUrl); } catch (_) {}
                          pledge("copy", null);
                        }} disabled={pledging}
                        data-testid="share-copy" className="qt-btn qt-btn-ghost">
                  <Copy size={14}/> Copy link
                </button>
              </div>
              {pledgeError && (
                <div className="mt-3 px-3 py-2 rounded text-sm"
                     data-testid="pledge-error"
                     style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
                  <AlertOctagon size={14} className="inline -mt-1 mr-2" />{pledgeError}
                </div>
              )}
              <p className="mt-3 qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
                You only get 1 free preview per (account, address) on the free tier.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { t: "Ed25519 is broken by Shor's", d: "Cosmos & Move-VM all use Ed25519/secp256k1 — same curves that fail under a CRQC." },
            { t: "Address ≠ public key — until you sign", d: "Most addresses are hash-protected. The first outgoing tx leaks the pubkey forever." },
            { t: "Migration runway is years, not days", d: "Want a hybrid signing roadmap? Schedule a free 30-min consult — no commitment." },
          ].map((x, i) => (
            <div key={i} className="qt-card">
              <ShieldAlert size={18} style={{ color: "var(--qt-cyan)" }} />
              <div className="qt-h2 text-lg mt-2">{x.t}</div>
              <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>{x.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Iter52.10 — result-aware upgrade copy. Returns a personalized headline
// + body for the in-result Pro upgrade tile, derived from the wallet's
// actual scan outcome. Three signal-driven variants:
//   1. pubkey_exposed=true → quantum-recoverable framing (highest urgency)
//   2. tier in (Critical, High) → remediation framing
//   3. tier in (Moderate, Low) → continuous-monitoring framing
function resultAwareHeadline(result) {
  const exposed = result?.scan?.data?.pubkey_exposed;
  const tier = result?.scan?.scoring?.tier;
  const chain = result?.scan?.chain_label || "this wallet";
  if (exposed) return `${chain} is quantum-recoverable. Pro shows the 17 mitigation paths.`;
  if (tier === "Critical" || tier === "High") {
    return `Your exposure tier is ${tier}. Pro tracks remediation across all 18 chains.`;
  }
  return `${chain} is currently hash-protected. Pro alerts you the moment that changes.`;
}

function resultAwareBody(result) {
  const exposed = result?.scan?.data?.pubkey_exposed;
  const tier = result?.scan?.scoring?.tier;
  if (exposed) {
    return "The watermarked preview shows your score — Pro unlocks the deterministic remediation plan, the chain-specific migration runway, and continuous monitoring so you know the day Ed25519 actually breaks.";
  }
  if (tier === "Critical" || tier === "High") {
    return "Pro turns your free score into a board-ready PDF + dynamic certificate, plus per-chain remediation guidance and a clear migration runway. Cancel anytime.";
  }
  return "You're safe today — but pubkey exposure is one transaction away. Atlas continuous monitoring + Pro deep-dive scans give you the migration runway before your wallet actually needs it.";
}


function TierRow({ free, pro, text }) {
  // Two-column tick marks: FREE column = ✓ if `free` true (mint), else ✗
  // (muted); PRO column = always ✓ in cyan. Text column on the right.
  return (
    <li className="grid grid-cols-12 gap-3 items-center" data-testid="tier-row">
      <div className="col-span-2 qt-mono text-[10px]" style={{ letterSpacing: "0.10em" }}>
        <span style={{ color: free ? "#1FDD83" : "var(--qt-text-3)", fontWeight: 700 }}>
          FREE {free ? "✓" : "—"}
        </span>
      </div>
      <div className="col-span-2 qt-mono text-[10px]" style={{ letterSpacing: "0.10em" }}>
        <span style={{ color: pro ? "var(--qt-cyan)" : "var(--qt-text-3)", fontWeight: 700 }}>
          PRO {pro ? "✓" : "—"}
        </span>
      </div>
      <div className="col-span-8" style={{ color: "var(--qt-text-2)" }}>{text}</div>
    </li>
  );
}

function ShareLink({ testid, href, bg, border, fg, label, Icon }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       data-testid={testid}
       style={{
         display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
         padding: "12px 14px", borderRadius: 6, fontWeight: 700, fontSize: 14,
         background: bg, border: `1px solid ${border}`, color: fg,
         transition: "transform .15s, box-shadow .15s, opacity .15s",
       }}
       onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 6px 20px ${border}40`; }}
       onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
      <Icon size={16} /> <span>{label}</span>
    </a>
  );
}

function PublicCopyButton({ text }) {
  const [done, setDone] = React.useState(false);
  const onClick = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1800); } catch (_) {}
  };
  return (
    <button onClick={onClick}
            data-testid="public-share-copy"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "12px 14px", borderRadius: 6, fontWeight: 700, fontSize: 14,
              background: done ? "#1FDD83" : "transparent",
              border: "1px solid #FFFFFF",
              color: done ? "#040914" : "#FFFFFF",
              cursor: "pointer", transition: "all .15s",
            }}>
      {done ? <><Check size={16}/> Copied</> : <><Copy size={16}/> Copy link</>}
    </button>
  );
}

